import {
  clipLogin,
  confirmMediaAgreement as confirmMediaAgreementRoute,
  fetchConfigurations,
  fetchVideoDownloadUrl,
  getMediaAgreement as getMediaAgreementRoute,
  logout as logoutRoute,
  modifyProfile as modifyProfileRoute,
  sendEmailCaptcha,
  sendSmsCaptcha,
  translateTitle,
  verifyKaloOauthToken,
} from "./http/api";
import { request, requestV2 } from "./index";
import { deviceType } from "@config";

// commonservice：登录、用户、配置、协议等通用接口集合。
// 已被 OpenAPI 覆盖且仍在使用的方法会内部委托给 `@apis/http/api` 的 defineRoute。
// 未在 OpenAPI 覆盖但仍被调用的方法保留旧 `request`/`requestV2` 实现。

// ===== 已迁移到 defineRoute（保留对外签名） =====

const fetchConfigs = async <Table>({ reqData }: { reqData: string[] }) => {
  const data = reqData.map((item) => ({ key: item }));
  return (await fetchConfigurations(data as any)) as unknown as Table;
};

const signup = async (data: {
  phoneCountryCode?: string;
  phoneNumber?: string;
  currency?: string;
  smsCode?: string;
  tcCode?: string;
  email?: string;
  emailCode?: string;
  loginMethod?: string; // EMAIL
  scene?: string;
}) => {
  delete data.scene;
  return await clipLogin({ body: data as unknown as any, query: {}, context: { toastDuration: 10 } });
};

const login = async (data: {
  phoneCountryCode?: string;
  phoneNumber?: string;
  smsCode?: string;
  loginMethod: "PASSWORD" | "EMAIL_PASSWORD" | "EMAIL" | "SMS" | "GOOGLE";
  password?: string;
  tcCode?: string;
  currency?: string;
  email?: string;
  emailCode?: string;
  scene?: string;
}) => {
  delete data.scene;
  return clipLogin({ body: data as unknown as any, query: {}, context: { silent: true } });
};

const tiktokLogin = async (data: { code: string; type: string; client?: string }) => {
  return await clipLogin({
    body: { code: data.code, loginMethod: data.client || "TIKTOK" } as any,
    query: {},
  });
};

const googleLogin = async (data: { code: string; type: string; client?: string }) => {
  return await clipLogin({
    body: { code: data.code, loginMethod: data.client || "GOOGLE" } as any,
    query: {},
  });
};

const verifyToken = async (data: { token: string }) => {
  return await verifyKaloOauthToken(data as any);
};

const sendSmsCode = async (data: {
  scene?: "signup" | "login" | "reset" | "verify" | "resetPassword";
  countryCode?: string;
  phoneNumber?: string;
  email?: string;
  response: string;
  captchaType?: string;
}) => {
  const isEmail = !!data.email;
  delete data.scene;
  if (isEmail) {
    return await sendEmailCaptcha(data as any);
  }
  return await sendSmsCaptcha(data as any);
};

const modifyProfile = async (data: {
  identity?: "CREATOR" | "SELLER" | "AGENCY";
  language?: string;
  currency?: string;
  industry?: string[];
  requirementIds?: string;
  platform?: string;
  referralCode?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
}) => {
  return await modifyProfileRoute(data as any);
};

const logout = () => {
  return logoutRoute();
};

const getMediaAgreement = async <T = string[]>() => {
  return (await getMediaAgreementRoute()) as unknown as T;
};

const confirmMediaAgreement = async (data: { version: string }) => {
  return await confirmMediaAgreementRoute(data as any);
};

const getDownloadVideoURL = async (reqData: { videoId: string; videoSource: string }) => {
  return await fetchVideoDownloadUrl(reqData as any);
};

const getTitleTranslate = async (reqData: { text: string; to: string }) => {
  return (await translateTitle(reqData as any)) as unknown as string;
};

// refreshToken 在 `apis/http/refreshTokenRequest.ts` 已有专用客户端用于无插件刷新；
// 这里保留旧 axios 实现以兼容 `apis/index.ts` 的 autoRefreshToken 链路。
const refreshToken = async () => {
  return request({
    method: "post",
    url: "/api/token/refresh",
  });
};

// ===== 仍未在 OpenAPI 覆盖、保留旧 `request`/`requestV2` 实现 =====

const verifyAccount = async (data: {
  loginMethod: "SMS" | "TIKTOK" | "EMAIL" | "GOOGLE";
  phoneCountryCode?: string;
  phoneNumber?: string;
  smsCode?: string;
  code?: string;
  currentPhoneNumber?: string;
  email?: string;
  emailCode?: string;
}) => {
  return request({
    method: "post",
    url: "/api/users/verify",
    data,
  });
};

// Customer Service
const fetchCustomerServiceContact = async () => {
  return requestV2<CustomerServiceContact>({ url: `/v1/countryV1/contacts`, params: { type: deviceType } });
};

// Reset Password
export interface VerifyForResetPasswordRequest {
  loginMethod: "EMAIL" | "SMS";
  email?: string;
  emailCode?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  smsCode?: string;
}

const verifyForResetPassword = async (data: VerifyForResetPasswordRequest) => {
  return requestV2({
    method: "post",
    url: `/user/verifyForResetPassword`,
    data: {
      ...data,
    },
  });
};

const resetPassword = async (password: string) => {
  return requestV2({ method: "post", url: `/user/resetPassword`, data: { password } });
};

const checkVerification = (data: {
  scene?: "signup" | "login" | "reset" | "addSubAccount" | "verify";
  phoneNumber?: string;
  smsCode?: string;
  phoneCountryCode?: string;
  loginMethod?: string;
  email?: string;
  emailCode?: string;
}) => {
  let url = "/api/checkVerification";
  if (data.email && data.scene === "login") url = "/email/checkLoginVerification";
  if (data.email && data.scene === "signup") url = "/email/checkSignupVerification";
  delete data.scene;
  return request({
    method: "post",
    url: url,
    data,
  });
};

const checkPhoneNumberIsExist = (data: { phoneCountryCode: string; phoneNumber: string }) => {
  return request({
    method: "post",
    url: "/user/checkPhoneNumberIsExist",
    data,
  });
};

const checkEmailIsExist = (data: { email: string }) => {
  return request({
    method: "post",
    url: "/user/checkEmailIsExist",
    data,
  });
};

const queryMembership = async <T>(reqData: unknown) => {
  return request<T>({ method: "post", url: `/user/queryMembership`, data: reqData });
};

const queryMemberships = async <T>() => {
  return request<T>({ method: "post", url: `/user/queryAllMemberships` });
};

const queryDevices = async <T>(reqData: unknown) => {
  return request<T>({ method: "post", url: `/api/queryDevicesByPhoneNumber`, data: reqData });
};

const checkGoogleGrecaptcha = async <T>(reqData: { captcha: string; captchaType?: string }) => {
  return request<T>({
    method: "post",
    url: `/user/checkVerifyCode`,
    data: reqData,
  });
};

const getGlobalNotify = async () => {
  return request({
    method: "post",
    url: "/homepage/dialog/queryList",
  });
};

// 微信登录：获取微信二维码
const getWxQrUrl = async () => {
  return request<{ sceneStr: string; qrCodeUrl: string }>({
    method: "get",
    url: "/user/wechat/qrUrl",
  });
};

// 轮询登录状态
const getWxLoginToken = async (reqData: { sceneStr: string }) => {
  return request<{ logined: boolean; success: boolean; exist: boolean; wxOpenId: string; wxUnionId: string }>({
    method: "get",
    url: "/user/wechat/getLoginToken",
    params: reqData,
  });
};

// 广告绑定微信
const fetchBindWechat = async (reqData: { sceneStr: string }) => {
  return request<{
    logined: boolean;
    success: boolean;
    exist: boolean;
    wxOpenId: string;
    wxUnionId: string;
    used: boolean;
  }>({
    method: "get",
    url: "/user/bindWechat",
    params: reqData,
  });
};

// Google Ads 相关接口
const signGoogleAds = async () => {
  return request<{ data: Record<string, any> }>({
    method: "get",
    url: "/gooads/signGoogleAds",
  });
};

const adsCallback = async (reqData: { key: string }) => {
  return request({
    method: "get",
    url: `/gooads/adsCallback?orderIds=${reqData.key}`,
  });
};

export default {
  fetchConfigs,
  login,
  refreshToken,
  tiktokLogin,
  googleLogin,
  verifyToken,
  signup,
  modifyProfile,
  logout,
  sendSmsCode,
  checkVerification,
  queryMembership,
  queryMemberships,
  queryDevices,
  checkGoogleGrecaptcha,
  verifyAccount,
  checkPhoneNumberIsExist,
  checkEmailIsExist,
  getGlobalNotify,
  getTitleTranslate,
  getWxQrUrl,
  getWxLoginToken,
  fetchBindWechat,
  signGoogleAds,
  adsCallback,
  getDownloadVideoURL,
  getMediaAgreement,
  confirmMediaAgreement,
  // V1 APIs
  fetchCustomerServiceContact,
  verifyForResetPassword,
  resetPassword,
};
